/**
 * TEMP-H-002: Control Signals Hook
 * 
 * Sends control signals with optimistic updates and error rollback.
 * Provides mutation functions for all human-in-the-loop controls.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import type { 
  ControlSignalType,
  ControlSignalResponse,
  ControlSignalError,
  PauseSignal,
  ResumeSignal,
  CancelSignal,
  SkipToStageSignal,
  ApproveSignal,
  RequestRevisionSignal,
  StartMusicMasterSignal,
  StartMusicSidechainSignal
} from '@/lib/control-api-types';
import { controlKeys } from '@/lib/control-api-types';
import { joinApiPath } from '@/lib/api-config';
import { useDashboardServerStore } from '@/stores/dashboard-server';

interface UseControlSignalsOptions {
  onSuccess?: (response: ControlSignalResponse) => void;
  onError?: (error: ControlSignalError) => void;
}

/**
 * Hook to send control signals with optimistic updates
 */
export function useControlSignals(executionId: string, options: UseControlSignalsOptions = {}) {
  const queryClient = useQueryClient();
  const addNotification = useDashboardServerStore(state => state.addNotification);

  // Generic signal mutation
  const signalMutation = useMutation({
    mutationFn: async ({ 
      signalType, 
      payload 
    }: { 
      signalType: ControlSignalType, 
      payload?: any 
    }): Promise<ControlSignalResponse> => {
      const response = await fetch(joinApiPath('executions', executionId, 'signals', signalType), {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: payload ? JSON.stringify(payload) : undefined,
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({
          error: `HTTP ${response.status}: ${response.statusText}`,
          code: 'NETWORK_ERROR',
          executionId,
          signalType
        }));
        
        // Enhanced error semantics for auth cases
        let enhancedError = { ...error };
        
        if (response.status === 401) {
          enhancedError = {
            error: 'Authentication required to send control signals',
            code: 'AUTH_REQUIRED',
            executionId,
            signalType
          };
        } else if (response.status === 403) {
          enhancedError = {
            error: 'You do not have permission to control this execution',
            code: 'PERMISSION_DENIED',
            executionId,
            signalType
          };
        }
        
        throw {
          ...enhancedError,
          executionId,
          signalType,
          isAuthError: response.status === 401 || response.status === 403
        } as ControlSignalError;
      }

      return response.json();
    },
    onSuccess: (response) => {
      // Show success notification
      addNotification({
        type: 'success',
        title: 'Action Completed',
        message: response.message,
        duration: 3000
      });

      // Invalidate queries to refresh state
      queryClient.invalidateQueries({
        queryKey: controlKeys.queries(executionId)
      });

      options.onSuccess?.(response);
    },
    onError: (error: ControlSignalError) => {
      // Show error notification
      addNotification({
        type: 'error',
        title: 'Action Failed',
        message: error.error || 'Failed to process control signal',
        duration: 5000
      });

      // Invalidate queries to revert optimistic updates
      queryClient.invalidateQueries({
        queryKey: controlKeys.queries(executionId)
      });

      options.onError?.(error);
    }
  });

  // Specific signal functions
  const pauseExecution = useCallback(async (signal: PauseSignal = {}) => {
    return signalMutation.mutateAsync({ 
      signalType: 'pause', 
      payload: signal 
    });
  }, [signalMutation]);

  const resumeExecution = useCallback(async (signal: ResumeSignal = {}) => {
    return signalMutation.mutateAsync({ 
      signalType: 'resume', 
      payload: signal 
    });
  }, [signalMutation]);

  const cancelExecution = useCallback(async (signal: CancelSignal = {}) => {
    return signalMutation.mutateAsync({ 
      signalType: 'cancel', 
      payload: signal 
    });
  }, [signalMutation]);

  const skipToStage = useCallback(async (signal: SkipToStageSignal) => {
    return signalMutation.mutateAsync({ 
      signalType: 'skip-to-stage', 
      payload: signal 
    });
  }, [signalMutation]);

  const approveStage = useCallback(async (signal: ApproveSignal) => {
    return signalMutation.mutateAsync({ 
      signalType: 'approve', 
      payload: signal 
    });
  }, [signalMutation]);

  const requestRevision = useCallback(async (signal: RequestRevisionSignal) => {
    return signalMutation.mutateAsync({ 
      signalType: 'request-revision', 
      payload: signal 
    });
  }, [signalMutation]);

  const startMusicMaster = useCallback(async (signal: StartMusicMasterSignal = {}) => {
    return signalMutation.mutateAsync({ 
      signalType: 'start-music-master', 
      payload: signal 
    });
  }, [signalMutation]);

  const startMusicSidechain = useCallback(async (signal: StartMusicSidechainSignal = {}) => {
    return signalMutation.mutateAsync({ 
      signalType: 'start-music-sidechain', 
      payload: signal 
    });
  }, [signalMutation]);

  return {
    // Individual signal functions
    pauseExecution,
    resumeExecution,
    cancelExecution,
    skipToStage,
    approveStage,
    requestRevision,
    startMusicMaster,
    startMusicSidechain,
    
    // Mutation state
    isLoading: signalMutation.isPending,
    error: signalMutation.error,
    reset: signalMutation.reset,
    
    // Last successful response
    lastResponse: signalMutation.data,
  };
}